import * as React from "react";
import { Stack, useTheme, IButtonStyles, IconButton, IStyle, mergeStyles, ITooltipHostStyles, TooltipHost } from "@fluentui/react";
import { useAppDispatch, useAppSelector } from "../hooks";
import { showSettingsDialog } from "../features/settings/settingsSlice";
import * as globalConfigActions from "../actions/globalConfigActions";
import { Utils } from "../Utils";
import { DeferredTooltipHost } from "./DeferredTooltipHost";

/**
 * The height of the header
 */
export const rcaHeaderHeight = 50;

/**
 * The Header bar component
 *
 * @returns - The component
 */
export const RcaHeader: React.FC = () => {
  const dispatch = useAppDispatch();
  const showNav = useAppSelector((state) => state.globalConfig.showNav);
  const { palette } = useTheme();

  const titleStyle: IStyle = {
    color: palette.white,
    backgroundColor: "transparent",
    fontSize: 16,
    paddingLeft: 16,
    paddingRight: 16,
    fontWeight: "bold",
    cursor: "default",
    fontFamily:
      "SegoeUI-SemiBold-final,Segoe UI Semibold,SegoeUI-Regular-final,Segoe UI,'Segoe UI Web (West European)',Segoe,-apple-system,BlinkMacSystemFont,Roboto,Helvetica Neue,Tahoma,Helvetica,Arial,sans-serif",
    [Utils.mobileQuery]: {
      paddingLeft: 2,
      paddingRight: 2,
    },
  };

  const titleBarButtonStyles: IButtonStyles = {
    root: {
      marginLeft: 10,
      marginRight: 10,
      borderWidth: 2,
      fontWeight: "bold",
      backgroundColor: "transparent",
      color: palette.white,
      [Utils.mobileQuery]: {
        marginLeft: 2,
        marginRight: 2,
      },
    },
    rootHovered: {
      backgroundColor: "transparent",
      color: palette.white,
    },
    rootPressed: {
      backgroundColor: "transparent",
      color: palette.white,
    },
    rootFocused: {
      backgroundColor: "transparent",
      border: "1px solid",
      borderColor: palette.neutralLight,
    },
  };

  const menuButtonContainerStyle: ITooltipHostStyles = {
    root: {
      [Utils.notMobileQuery]: {
        display: "none",
      },
    },
  };

  const menuButtonTitle = showNav ? ClientStrings.HideMenu : ClientStrings.ShowMenu;
  return (
    <Stack
      horizontal
      verticalAlign="center"
      horizontalAlign="space-between"
      styles={{ root: { minHeight: rcaHeaderHeight, backgroundColor: palette.neutralPrimaryAlt, color: palette.neutralLight } }}
    >
      <DeferredTooltipHost content={menuButtonTitle} styles={menuButtonContainerStyle}>
        <IconButton
          iconProps={{ iconName: "CollapseMenu" }}
          styles={titleBarButtonStyles}
          ariaLabel={menuButtonTitle}
          onClick={(): void => dispatch(globalConfigActions.toggleNav())}
        />
      </DeferredTooltipHost>
      <h1 className={mergeStyles(titleStyle)}>{WebsiteStrings.ProductTitle}</h1>
      <Stack horizontal>
        <TooltipHost content={ClientStrings.SettingsButtonAriaLabel}>
          <IconButton
            iconProps={{ iconName: "Settings" }}
            styles={titleBarButtonStyles}
            ariaLabel={ClientStrings.SettingsButtonAriaLabel}
            onClick={(): void => {
              dispatch(showSettingsDialog());
            }}
          />
        </TooltipHost>
        <TooltipHost content={ClientStrings.HelpButtonAriaLabel}>
          <IconButton
            href="https://technet.microsoft.com/library/dd439364.aspx"
            target="_blank"
            iconProps={{ iconName: "Help" }}
            styles={titleBarButtonStyles}
            ariaLabel={ClientStrings.HelpButtonAriaLabel}
          />
        </TooltipHost>
      </Stack>
    </Stack>
  );
};

export default RcaHeader;
